import React, { useEffect, useState } from 'react';
import { brokerApi, marketApi, aiApi } from './api';
import TickerChartCard from './TickerChartCard';
import Modal from './Modal';

const ORDER_TYPES = [
  { id: 'market', label: 'Market' },
  { id: 'limit', label: 'Limit' },
  { id: 'stop', label: 'Stop' },
  { id: 'stop_limit', label: 'Stop-Limit' },
];

export default function ExecuteTrade({ onViewOrders, prefill }) {
  const [ticker, setTicker] = useState(prefill?.symbol || 'AAPL');
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quantity, setQuantity] = useState(prefill?.qty || 1);
  const [side, setSide] = useState(prefill?.side || 'buy');
  const [orderType, setOrderType] = useState('market');
  const [timeInForce, setTimeInForce] = useState('day');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState(null);
  const [check, setCheck] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState(null);

  useEffect(() => {
    brokerApi.account().then(setAccount).catch(() => {});
    brokerApi.positions().then(setPositions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ticker) return;
    const handle = setTimeout(() => {
      marketApi
        .quote(ticker.toUpperCase())
        .then((q) => {
          setQuote(q);
          setQuoteError(null);
        })
        .catch((err) => {
          setQuote(null);
          setQuoteError(err.message);
        });
    }, 400);
    return () => clearTimeout(handle);
  }, [ticker]);

  useEffect(() => {
    setCheck(null);
  }, [ticker, side, quantity, orderType]);

  const referencePrice = orderType === 'limit' || orderType === 'stop_limit' ? limitPrice : quote?.price;
  const estimatedTotal = referencePrice ? (referencePrice * quantity).toFixed(2) : null;

  const runPretradeCheck = async () => {
    setCheckLoading(true);
    setCheckError(null);
    try {
      const holdings = Object.fromEntries((positions || []).map((p) => [p.symbol, Number(p.qty)]));
      const data = await aiApi.tradeCheck({
        symbol: ticker.toUpperCase(),
        side,
        qty: Number(quantity),
        price: Number(referencePrice || quote?.price || 0),
        positions: holdings,
        cash: account ? Number(account.cash) : undefined,
      });
      setCheck(data);
    } catch (err) {
      setCheckError(err.message);
    } finally {
      setCheckLoading(false);
    }
  };

  const handleOpenPreview = (e) => {
    e.preventDefault();
    setResult(null);
    setShowPreview(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      const order = await brokerApi.placeOrder({
        symbol: ticker.toUpperCase(),
        qty: quantity,
        side,
        type: orderType,
        timeInForce,
        limitPrice: limitPrice || undefined,
        stopPrice: stopPrice || undefined,
      });
      setResult({
        type: 'success',
        message: `${side.toUpperCase()} order submitted for ${quantity} ${ticker.toUpperCase()} — status: ${order.status}`,
      });
      setShowPreview(false);
      brokerApi.account().then(setAccount).catch(() => {});
      brokerApi.positions().then(setPositions).catch(() => {});
    } catch (err) {
      setResult({ type: 'error', message: err.message });
      setShowPreview(false);
    } finally {
      setSubmitting(false);
    }
  };

  const existingPosition = positions?.find((p) => p.symbol === ticker.toUpperCase());
  const existingValue = existingPosition ? Number(existingPosition.market_value) : 0;
  const tradeValue = Number(estimatedTotal || 0);
  const resultingValue = side === 'buy' ? existingValue + tradeValue : Math.max(existingValue - tradeValue, 0);
  const portfolioValue = account ? Number(account.portfolio_value) : null;
  const resultingPct = portfolioValue ? (resultingValue / portfolioValue) * 100 : null;
  const buyingPowerPct = account?.buying_power ? (tradeValue / Number(account.buying_power)) * 100 : null;
  const insufficientFunds = side === 'buy' && account && tradeValue > Number(account.buying_power);
  const insufficientShares =
    side === 'sell' && (!existingPosition || Number(quantity) > Number(existingPosition.qty));

  return (
    <div className="module-container">
      <div className="module-header">
        <h2>Place Order</h2>
        <p>
          Orders execute live through your connected Alpaca account.{' '}
          {onViewOrders && (
            <button type="button" className="text-button" onClick={onViewOrders} style={{ padding: 0 }}>
              View order history →
            </button>
          )}
        </p>
      </div>

      <div className="trade-layout">
        <form className="trade-form" onSubmit={handleOpenPreview}>
          <div className="trade-type-selector">
            <button type="button" className={`type-button ${side === 'buy' ? 'active buy' : ''}`} onClick={() => setSide('buy')}>
              BUY
            </button>
            <button type="button" className={`type-button ${side === 'sell' ? 'active sell' : ''}`} onClick={() => setSide('sell')}>
              SELL
            </button>
          </div>

          <div className="form-grid">
            <div className="input-group">
              <label>Ticker Symbol</label>
              <input type="text" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} maxLength={6} required />
            </div>
            <div className="input-group">
              <label>Quantity</label>
              <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="1" step="1" required />
            </div>
            <div className="input-group">
              <label>Live Price</label>
              <div className="live-price">
                {quoteError ? <span className="wl-error">unavailable</span> : quote ? `$${quote.price?.toFixed(2)}` : <span className="skeleton-bar" />}
              </div>
            </div>
          </div>

          <div className="input-group">
            <label>Order Type</label>
            <div className="order-type-selector">
              {ORDER_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={orderType === t.id ? 'active' : ''}
                  onClick={() => setOrderType(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {(orderType === 'limit' || orderType === 'stop_limit') && (
            <div className="input-group">
              <label>Limit Price</label>
              <input type="number" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} step="0.01" min="0" required />
            </div>
          )}
          {(orderType === 'stop' || orderType === 'stop_limit') && (
            <div className="input-group">
              <label>Stop Price</label>
              <input type="number" value={stopPrice} onChange={(e) => setStopPrice(e.target.value)} step="0.01" min="0" required />
            </div>
          )}

          <div className="input-group">
            <label>Time in Force</label>
            <select value={timeInForce} onChange={(e) => setTimeInForce(e.target.value)}>
              <option value="day">Day</option>
              <option value="gtc">Good till canceled</option>
            </select>
          </div>

          <div className="trade-summary">
            <div className="summary-item">
              <span>Order Type</span>
              <span>{ORDER_TYPES.find((t) => t.id === orderType)?.label}</span>
            </div>
            <div className="summary-item">
              <span>Estimated Total</span>
              <span className="amount">{estimatedTotal ? `$${estimatedTotal}` : '—'}</span>
            </div>
          </div>

          <button type="button" className="secondary-button" onClick={runPretradeCheck} disabled={checkLoading || !ticker}>
            {checkLoading ? 'Checking…' : '✦ Run AI pre-trade check'}
          </button>

          {checkError && <div className="error-box">{checkError}</div>}

          {check && (
            <div className="pretrade-card">
              <span className="ai-badge">✦ AI Pre-Trade Check</span>
              <div style={{ marginBottom: 8 }}>
                <span className={`recommendation-badge ${(check.recommendation || '').toLowerCase()}`}>
                  {(check.recommendation || 'N/A').replace(/_/g, ' ')}
                </span>
              </div>
              <p className="reasoning">{check.riskNote}</p>
              {check.flags?.map((f) => (
                <span key={f} className="flag-chip">
                  {f}
                </span>
              ))}
            </div>
          )}

          <button type="submit" className={`primary-button trade-button ${side}`}>
            Review {side.toUpperCase()} {quantity} shares of {ticker}
          </button>

          {result && (
            <div className={`result-message ${result.type}`}>
              <span className="result-icon">{result.type === 'success' ? '✓' : '✗'}</span>
              <p>{result.message}</p>
            </div>
          )}
        </form>

        <TickerChartCard symbol={ticker} tall />
      </div>

      <Modal open={showPreview} onClose={() => setShowPreview(false)} title="Confirm order">
        <div className="trade-summary" style={{ marginBottom: 16 }}>
          <div className="summary-item">
            <span>Order</span>
            <span className="amount">
              {side.toUpperCase()} {quantity} {ticker.toUpperCase()}
            </span>
          </div>
          <div className="summary-item">
            <span>Order Type</span>
            <span>{ORDER_TYPES.find((t) => t.id === orderType)?.label}</span>
          </div>
          <div className="summary-item">
            <span>Estimated Total</span>
            <span className="amount">{estimatedTotal ? `$${estimatedTotal}` : '—'}</span>
          </div>
          {portfolioValue != null && (
            <div className="summary-item">
              <span>{ticker.toUpperCase()} would become</span>
              <span className="amount">{resultingPct?.toFixed(1)}% of portfolio</span>
            </div>
          )}
          {buyingPowerPct != null && side === 'buy' && (
            <div className={`summary-item ${insufficientFunds ? 'insufficient' : ''}`}>
              <span>Buying power used</span>
              <span className="amount">{buyingPowerPct.toFixed(1)}%</span>
            </div>
          )}
        </div>

        {insufficientFunds && <div className="error-box">This order exceeds your available buying power.</div>}
        {insufficientShares && <div className="error-box">You don't own enough shares to sell this quantity.</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="secondary-button" onClick={() => setShowPreview(false)} style={{ flex: 1 }}>
            Cancel
          </button>
          <button
            type="button"
            className={`primary-button trade-button ${side}`}
            style={{ flex: 1 }}
            disabled={submitting || insufficientFunds || insufficientShares}
            onClick={handleConfirmSubmit}
          >
            {submitting ? 'Submitting…' : 'Confirm & Submit'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
